import { DecodedInstruction } from '../InstructionDecoder';

export interface BasicBlock {
  id: number;
  instructions: DecodedInstruction[];
  successors: BasicBlock[];
  predecessors: BasicBlock[];
  isEntry: boolean;
  isExit: boolean;
  wasmBlockDepth: number;
  brTableTargets: BasicBlock[] | null;
  brTableDefault: BasicBlock | null;
}

export interface ControlFlowGraph {
  entry: BasicBlock;
  exit: BasicBlock;
  blocks: BasicBlock[];
  blockEndTargets: Map<number, number>;  // blockId → WASM scope depth at which this block is an end-target
}

interface BlockScope {
  kind: 'block' | 'loop' | 'if' | 'try';
  headerBlock: BasicBlock;
  endTarget: BasicBlock | null;
  catchBlock: BasicBlock | null;
  thenBlock: BasicBlock | null;
  elseBlock: BasicBlock | null;
}

function addEdge(from: BasicBlock, to: BasicBlock): void {
  if (!from.successors.includes(to)) {
    from.successors.push(to);
  }
  if (!to.predecessors.includes(from)) {
    to.predecessors.push(from);
  }
}

/**
 * Translates WASM structured control flow into a flat CFG.
 * WASM uses block/loop/if/br scoping rather than arbitrary gotos, so this maintains
 * a scope stack that maps br depths to target blocks. Each WASM scope kind (block, loop,
 * if, try) pushes a scope entry; br targets resolve to the end-block (for block/if/try)
 * or the header (for loop). After construction, unreachable blocks are pruned.
 */
export function buildControlFlowGraph(instructions: DecodedInstruction[]): ControlFlowGraph {
  return new ControlFlowGraphBuilder().build(instructions);
}

class ControlFlowGraphBuilder {
  private nextBlockId = 0;
  private readonly entry: BasicBlock;
  private readonly exit: BasicBlock;
  private readonly allBlocks: BasicBlock[];
  private readonly scopeStack: BlockScope[] = [];
  private readonly blockEndTargets = new Map<number, number>();  // blockId → scope depth
  private currentBlock: BasicBlock;

  constructor() {
    this.entry = this.createBlock();
    this.entry.isEntry = true;
    this.exit = this.createBlock();
    this.exit.isExit = true;
    this.allBlocks = [this.entry, this.exit];
    this.currentBlock = this.entry;
  }

  build(instructions: DecodedInstruction[]): ControlFlowGraph {
    for (const instruction of instructions) {
      this.process(instruction);
    }
    if (this.currentBlock.successors.length === 0 && this.currentBlock !== this.exit) {
      addEdge(this.currentBlock, this.exit);
    }
    return this.pruneAndRenumber();
  }

  private process(instruction: DecodedInstruction): void {
    switch (instruction.opCode.mnemonic) {
      case 'try': this.handleTry(instruction); return;
      case 'catch': case 'catch_all': this.handleCatch(); return;
      case 'throw': case 'rethrow': this.handleThrow(instruction); return;
      case 'delegate': this.handleDelegate(); return;
      case 'block': case 'loop': this.handleBlockOrLoop(instruction); return;
      case 'if': this.handleIf(instruction); return;
      case 'else': this.handleElse(); return;
      case 'end': this.handleEnd(); return;
      case 'br': this.handleBr(instruction); return;
      case 'br_if': this.handleBrIf(instruction); return;
      case 'br_table': this.handleBrTable(instruction); return;
      case 'return': this.handleReturn(instruction); return;
      case 'unreachable': this.handleUnreachable(instruction); return;
      default: this.currentBlock.instructions.push(instruction);
    }
  }

  private createBlock(): BasicBlock {
    return {
      id: this.nextBlockId++,
      instructions: [],
      successors: [],
      predecessors: [],
      isEntry: false,
      isExit: false,
      wasmBlockDepth: 0,
      brTableTargets: null,
      brTableDefault: null,
    };
  }

  private startNewBlock(): BasicBlock {
    const block = this.createBlock();
    block.wasmBlockDepth = this.scopeStack.length;
    this.allBlocks.push(block);
    return block;
  }

  private resolveBreakTarget(depth: number): BasicBlock | null {
    const targetIndex = this.scopeStack.length - 1 - depth;
    if (targetIndex < 0 || targetIndex >= this.scopeStack.length) {
      return null;
    }
    const scope = this.scopeStack[targetIndex];
    if (scope.kind === 'loop') {
      return scope.headerBlock;
    }
    if (!scope.endTarget) {
      scope.endTarget = this.startNewBlock();
    }
    return scope.endTarget;
  }

  private pushScope(kind: BlockScope['kind'], headerBlock: BasicBlock): BlockScope {
    const scope: BlockScope = { kind, headerBlock, endTarget: null, catchBlock: null, thenBlock: null, elseBlock: null };
    this.scopeStack.push(scope);
    return scope;
  }

  private handleTry(instruction: DecodedInstruction): void {
    this.currentBlock.instructions.push(instruction);
    const tryBody = this.startNewBlock();
    const catchBody = this.startNewBlock();
    addEdge(this.currentBlock, tryBody);
    addEdge(this.currentBlock, catchBody);
    const scope = this.pushScope('try', this.currentBlock);
    scope.catchBlock = catchBody;
    this.currentBlock = tryBody;
  }

  private handleCatch(): void {
    const scope = this.scopeStack[this.scopeStack.length - 1];
    if (scope && scope.kind === 'try' && scope.catchBlock) {
      if (!scope.endTarget) {
        scope.endTarget = this.startNewBlock();
      }
      addEdge(this.currentBlock, scope.endTarget);
      this.currentBlock = scope.catchBlock;
      scope.catchBlock = null;
    }
  }

  private handleThrow(instruction: DecodedInstruction): void {
    this.currentBlock.instructions.push(instruction);
    this.currentBlock = this.startNewBlock();
  }

  private handleDelegate(): void {
    // delegate terminates the current try block (pops one scope). The depth immediate
    // identifies which enclosing try re-catches the exception — that affects runtime
    // exception routing but not scope nesting, so this acts like 'end' for the try.
    if (this.scopeStack.length === 0) {
      return;
    }
    const scope = this.scopeStack.pop()!;
    if (!scope.endTarget) {
      scope.endTarget = this.startNewBlock();
    }
    this.blockEndTargets.set(scope.endTarget.id, this.scopeStack.length);
    if (this.currentBlock !== scope.endTarget) {
      addEdge(this.currentBlock, scope.endTarget);
    }
    this.currentBlock = scope.endTarget;
  }

  private handleBlockOrLoop(instruction: DecodedInstruction): void {
    this.currentBlock.instructions.push(instruction);
    const continueBlock = this.startNewBlock();
    addEdge(this.currentBlock, continueBlock);
    this.pushScope(instruction.opCode.mnemonic as 'block' | 'loop', continueBlock);
    this.currentBlock = continueBlock;
  }

  private handleIf(instruction: DecodedInstruction): void {
    this.currentBlock.instructions.push(instruction);
    const thenBlock = this.startNewBlock();
    const elseBlock = this.startNewBlock();
    addEdge(this.currentBlock, thenBlock);
    addEdge(this.currentBlock, elseBlock);
    const scope = this.pushScope('if', this.currentBlock);
    scope.thenBlock = thenBlock;
    scope.elseBlock = elseBlock;
    this.currentBlock = thenBlock;
  }

  private handleElse(): void {
    const scope = this.scopeStack[this.scopeStack.length - 1];
    if (scope && scope.kind === 'if' && scope.elseBlock) {
      if (!scope.endTarget) {
        scope.endTarget = this.startNewBlock();
      }
      addEdge(this.currentBlock, scope.endTarget);
      this.currentBlock = scope.elseBlock;
    }
  }

  private handleEnd(): void {
    if (this.scopeStack.length === 0) {
      addEdge(this.currentBlock, this.exit);
      return;
    }
    const scope = this.scopeStack.pop()!;
    if (!scope.endTarget) {
      scope.endTarget = this.startNewBlock();
    }
    // Record this endTarget's scope depth for dispatch detection.
    this.blockEndTargets.set(scope.endTarget.id, this.scopeStack.length);
    if (this.currentBlock !== scope.endTarget) {
      addEdge(this.currentBlock, scope.endTarget);
    }
    if (scope.kind === 'if' && scope.elseBlock) {
      const elseBlock = scope.elseBlock;
      if (elseBlock.successors.length === 0 && elseBlock.instructions.length === 0) {
        addEdge(elseBlock, scope.endTarget);
      }
    }
    if (scope.kind === 'try' && scope.catchBlock) {
      addEdge(scope.catchBlock, scope.endTarget);
    }
    this.currentBlock = scope.endTarget;
  }

  private handleBr(instruction: DecodedInstruction): void {
    this.currentBlock.instructions.push(instruction);
    const immediate = instruction.immediates;
    const target = immediate.type === 'RelativeDepth' ? this.resolveBreakTarget(immediate.depth) : null;
    if (target) {
      addEdge(this.currentBlock, target);
    }
    this.currentBlock = this.startNewBlock();
  }

  private handleBrIf(instruction: DecodedInstruction): void {
    this.currentBlock.instructions.push(instruction);
    const immediate = instruction.immediates;
    const target = immediate.type === 'RelativeDepth' ? this.resolveBreakTarget(immediate.depth) : null;
    const fallthrough = this.startNewBlock();
    if (target) {
      addEdge(this.currentBlock, target);
    }
    addEdge(this.currentBlock, fallthrough);
    this.currentBlock = fallthrough;
  }

  private handleBrTable(instruction: DecodedInstruction): void {
    this.currentBlock.instructions.push(instruction);
    if (instruction.immediates.type !== 'BranchTable') {
      this.currentBlock = this.startNewBlock();
      return;
    }
    const targets = instruction.immediates.targets;
    const defaultDepth = instruction.immediates.defaultTarget;

    const resolvedTargets: BasicBlock[] = [];
    const visitedTargets = new Set<BasicBlock>();
    for (const depth of targets) {
      const target = this.resolveBreakTarget(depth);
      if (target) {
        resolvedTargets.push(target);
        if (!visitedTargets.has(target)) {
          addEdge(this.currentBlock, target);
          visitedTargets.add(target);
        }
      }
    }
    const defaultTarget = this.resolveBreakTarget(defaultDepth);
    if (defaultTarget && !visitedTargets.has(defaultTarget)) {
      addEdge(this.currentBlock, defaultTarget);
    }

    this.currentBlock.brTableTargets = resolvedTargets;
    this.currentBlock.brTableDefault = defaultTarget;
    this.currentBlock = this.startNewBlock();
  }

  private handleReturn(instruction: DecodedInstruction): void {
    this.currentBlock.instructions.push(instruction);
    addEdge(this.currentBlock, this.exit);
    this.currentBlock = this.startNewBlock();
  }

  private handleUnreachable(instruction: DecodedInstruction): void {
    this.currentBlock.instructions.push(instruction);
    this.currentBlock = this.startNewBlock();
  }

  private pruneAndRenumber(): ControlFlowGraph {
    const reachable = new Set<BasicBlock>();
    const worklist: BasicBlock[] = [this.entry];
    while (worklist.length > 0) {
      const block = worklist.pop()!;
      if (reachable.has(block)) {
        continue;
      }
      reachable.add(block);
      for (const successor of block.successors) {
        worklist.push(successor);
      }
    }

    const liveBlocks = this.allBlocks.filter(block => reachable.has(block));
    const oldIdToNewId = new Map<number, number>();
    for (let blockIndex = 0; blockIndex < liveBlocks.length; blockIndex++) {
      oldIdToNewId.set(liveBlocks[blockIndex].id, blockIndex);
      liveBlocks[blockIndex].id = blockIndex;
    }
    // A function that never returns normally (every path ends in unreachable) leaves the exit
    // sink unreachable, so it is not among the renumbered live blocks and keeps its original
    // id. Re-id it beyond the live range so it can't collide with a real block — a collision
    // makes that block look like the exit, which injects a spurious early return and truncates
    // structuring (the whole body becomes unreachable).
    if (!reachable.has(this.exit)) {
      this.exit.id = liveBlocks.length;
    }

    const renumberedBlockEndTargets = new Map<number, number>();
    for (const [oldBlockId, scopeDepth] of this.blockEndTargets) {
      const newBlockId = oldIdToNewId.get(oldBlockId);
      if (newBlockId !== undefined) {
        renumberedBlockEndTargets.set(newBlockId, scopeDepth);
      }
    }

    return {
      entry: liveBlocks[0],
      exit: this.exit,
      blocks: liveBlocks,
      blockEndTargets: renumberedBlockEndTargets,
    };
  }
}
